import React, { memo, useContext } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useDerivedValue, } from 'react-native-reanimated';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import ToolTip from 'design-system/ToolTip';
import { CardView } from 'design-system/CardView';
import UpgradeButton from 'design-system/UpgradeButton';
import { CurrencyAmountText, IconSegmentedControl } from 'design-system';
import { IconActivity, IconFourBars } from 'design-system/icons';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { WalkthroughContext } from 'features/feed/components/walkthrough/context';
import { isWeb } from '../../../constants';
import { setShowOverTimeRunningSum } from '../actions';
import { selectChartType, selectShowOverTimeRunningSum } from '../selectors';
import { ChartType, chartTypeToMinHeight } from '../types';
import IncomeSpendingComparisonChart from './chart/IncomeSpendingComparisonChart';
import CategoryPieChart from './chart/CategoryPieChart';
import SpendingOverTimeChart from './chart/SpendingOverTimeChart';
const strings = {
    incomeSpendingTooltip: {
        title: 'Summary value',
        subtitle: 'This is your total income minus your spending for the period you have selected. The default is calendar months. This value can also help you understand if you are saving or spending more than you make.',
    },
    spendingOverTimeTooltip: {
        title: 'Summary value',
        subtitle: 'This is your total spending from the last period minus the spending in the selected period',
    },
};
export const Heading = ({ aboveAmount, toolTip, amount, showPlus, }) => (<Animated.View style={styles.headingContainer}>
    <View style={styles.headingAboveAmount}>
      <Text Secondary Text-14 numberOfLines={1}>
        {aboveAmount}
      </Text>
      {toolTip ? (<>
          <Spacer w={4}/>
          <ToolTip title={toolTip.title} subtitle={toolTip.subtitle} small/>
        </>) : null}
    </View>
    <View style={styles.headingAmount}>
      <CurrencyAmountText value={amount} showPlus={showPlus} showMinus size="M"/>
    </View>
  </Animated.View>);
const AnalyticsCarouselItem = ({ index, positionAnimatedValue, currency, income, spending, previousSpending, value, monthlyCategories, spendingBreakdown, spendingBreakdownPrevious, currentFrom, currentTo, previousFrom, previousTo, monthlyTotalsStep, blurValue, onPressUpgradeForSearch, }) => {
    const isInFocus = useDerivedValue(() => positionAnimatedValue.value === index, [positionAnimatedValue, index]);
    const isInWalkthrough = useContext(WalkthroughContext);
    const chartTypeToShow = useAppSelector(selectChartType);
    const showOverTimeRunningSum = useAppSelector(selectShowOverTimeRunningSum);
    const dispatch = useAppDispatch();
    if (blurValue) {
        return (<CardView withPadding style={[
                styles.blurCard,
                {
                    minHeight: rem(chartTypeToMinHeight[chartTypeToShow]),
                },
            ]}>
        <UpgradeButton chipText="Enhanced tracking" onCard onPress={onPressUpgradeForSearch}/>
      </CardView>);
    }
    if (chartTypeToShow === ChartType.income_spending) {
        return (<CardView noMargin={isInWalkthrough}>
        <View style={styles.row}>
          <Heading aboveAmount="Summary" amount={{ amount: value, currency }} toolTip={strings.incomeSpendingTooltip} showPlus/>
        </View>
        <Spacer h={24}/>
        <IncomeSpendingComparisonChart income={income} spending={spending} currency={currency} blurValue={blurValue}/>
      </CardView>);
    }
    if (chartTypeToShow === ChartType.over_time) {
        return (<CardView noMargin={isInWalkthrough}>
        <View style={styles.row}>
          <View>
            <Heading aboveAmount="Summary" amount={{
                amount: (previousSpending ?? 0) - spending,
                currency,
            }} toolTip={strings.spendingOverTimeTooltip} showPlus/>
          </View>
          {monthlyTotalsStep === 'year' ? (<View style={styles.yearlyChartControl}>
              <IconSegmentedControl values={[
                    {
                        IconComponent: (props) => <IconActivity alt {...props}/>,
                        value: true,
                    },
                    { IconComponent: IconFourBars, value: false },
                ]} value={showOverTimeRunningSum} onValueChange={(value) => {
                    dispatch(setShowOverTimeRunningSum(value));
                }}/>
            </View>) : null}
        </View>
        <Spacer h={24}/>
        <View style={styles.spendingOverTimeChartContainer}>
          <SpendingOverTimeChart monthlyTotalsStep={monthlyTotalsStep} current={spendingBreakdown} currentSpending={spending} previousSpending={previousSpending} previous={spendingBreakdownPrevious} currency={currency} currentFrom={currentFrom} currentTo={currentTo} previousFrom={previousFrom} previousTo={previousTo} blurValue={blurValue} showRunningSum={monthlyTotalsStep !== 'year' || showOverTimeRunningSum}/>
        </View>
      </CardView>);
    }
    if (chartTypeToShow === ChartType.categories) {
        return (<CardView noMargin={isInWalkthrough}>
        <View style={styles.row}>
          <Heading aboveAmount="Total spent" amount={{ amount: spending, currency }} showPlus={false}/>
        </View>
        <Spacer h={24}/>
        <View style={styles.pieChartContainer}>
          <CategoryPieChart isInFocus={isInFocus} currency={currency} monthlyCategories={monthlyCategories} blurValue={blurValue}/>
          <Spacer h={24}/>
        </View>
      </CardView>);
    }
    return null;
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    blurCard: {
        justifyContent: 'center',
        // the web app has floating controls on both sides
        paddingHorizontal: isWeb ? rem(60) : undefined,
    },
    spendingOverTimeChartContainer: {
        alignItems: 'center',
    },
    pieChartContainer: {
        marginTop: -rem(16),
    },
    headingContainer: {
        padding: rem(16),
        paddingBottom: rem(8),
    },
    headingAboveAmount: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    headingAmount: {
        flexDirection: 'row',
        paddingBottom: rem(2),
    },
    yearlyChartControl: {
        margin: rem(8),
    },
});
export default memo(AnalyticsCarouselItem);
